import { IConfigs } from './config.model';

/**
 * Tenant specific configurations. These items are configured in the system admin portal.
 *
 *  - allow manufacturer combinations
 *  - enforce badge scanning
 *  - enforce manual entry validation
 *  - expiration policy
 *  - feature flags
 *  - order validation
 *  - product calorie range
 *  - regexes
 *     - badges
 *     - wrist
 */
export class TenantConfigs {
	info: TenantInfo = new TenantInfo();
	expirationPolicy: ExpirationPolicy = new ExpirationPolicy();
	allowManualEntryFreeText: boolean;
	allowMultiManufacturerCombinations: boolean;
	badgeRegex: string; // "@\"^\\d{10}$\""
	wristbandRegex: string;
	enforceBadgeScanning: boolean;
	productCalorieMax: number;
	productCalorieMin: number;
	validateOrders: boolean;
	enforceManualEntryOnValidationFailure: boolean;
	defaultDivideMilkToggleValue: boolean;
	nurseValidLoginExpiration: number; //168
	requireTwoFactorAuthentication: boolean;
	decimalPlaceForVolumeDisplay: number;
	overfillVolume: number;

	constructor(initial: IConfigs = {} as IConfigs) {
		const { tenant } = initial;

		if (!tenant) {
			return;
		}

		// Set TenantInfo
		this.info = new TenantInfo(tenant);

		// Set ExpirationPolicy
		this.expirationPolicy = new ExpirationPolicy(tenant);

		Object.keys(tenant).forEach((key) => {
			this[key] = tenant[key];
		});
	}

	toJSON(): ITenantConfigs {
		return {
			...this.info.toJSON(),
			...this.expirationPolicy.toJSON(),
			allowManualEntryFreeText: this.allowManualEntryFreeText,
			allowMultiManufacturerCombinations:
				this.allowMultiManufacturerCombinations,
			badgeRegex: this.badgeRegex,
			wristbandRegex: this.wristbandRegex,
			enforceBadgeScanning: this.enforceBadgeScanning,
			productCalorieMax: this.productCalorieMax,
			productCalorieMin: this.productCalorieMin,
			validateOrders: this.validateOrders,
			enforceManualEntryOnValidationFailure:
				this.enforceManualEntryOnValidationFailure,
			defaultDivideMilkToggleValue: this.defaultDivideMilkToggleValue,
			nurseValidLoginExpiration: this.nurseValidLoginExpiration,
			decimalPlaceForVolumeDisplay: this.decimalPlaceForVolumeDisplay,
			overfillVolume: this.overfillVolume,
		};
	}
}

/**
 * Opted on calling this "configs" instead of "settings" to imply that these are
 * system-type configurations and not user configurations.
 */
export interface ITenantConfigs {
	active: boolean;
	allowManualEntryFreeText: boolean;
	allowMultiManufacturerCombinations: boolean;
	badgeRegex: string;
	wristbandRegex: string;
	created: string;
	createdBy: string;
	enforceBadgeScanning: boolean;
	expirationHoursFormula: number;
	expirationHoursPowderedFormulaOpened: number;
	expirationHoursFreshEbm: number;
	expirationHoursStableDbmOpen: number;
	expirationHoursStableDbmFortifiedWithHM: number;
	expirationHoursStableDbmFortifiedWithoutHM: number;
	expirationHoursFreshEbmFortifiedWithHM: number;
	expirationHoursFreshEbmFortifiedWithoutHM: number;
	expirationHoursFrozenEbm: number;
	expirationHoursThawedDbm: number;
	expirationHoursThawedDbmFortifiedWithHM: number;
	expirationHoursThawedDbmFortifiedWithoutHM: number;
	expirationHoursThawedEbm: number;
	expirationHoursThawedEbmFortified: number;
	expirationHoursWater: number;
	id: string;
	modified: string;
	modifiedBy: string;
	name: string;
	productCalorieMax: number;
	productCalorieMin: number;
	validateOrders: boolean;
	enforceManualEntryOnValidationFailure: boolean;
	defaultDivideMilkToggleValue: boolean;
	nurseValidLoginExpiration: number;
	decimalPlaceForVolumeDisplay: number;
	overfillVolume: number;
}

export class TenantInfo {
	active: boolean;
	created: string;
	createdBy: string;
	id: string;
	modified: string;
	modifiedBy: string;
	name: string;

	constructor(initial: ITenantInfo = {} as ITenantInfo) {
		if (!initial) {
			return;
		}

		Object.keys(initial).forEach((key) => {
			this[key] = initial[key];
		});
	}

	toJSON(): ITenantInfo {
		return {
			active: this.active,
			created: this.created,
			createdBy: this.createdBy,
			id: this.id,
			modified: this.modified,
			modifiedBy: this.modifiedBy,
			name: this.name,
		};
	}
}

export interface ITenantInfo {
	active: boolean;
	created: string;
	createdBy: string;
	id: string;
	modified: string;
	modifiedBy: string;
	name: string;
}

/**
 * Expiration policy are the tenant-defined expiration duration (in hours) for
 * each product type.
 */
export class ExpirationPolicy {
	formula: number;
	powderedFormulaOpened: number;
	freshEbm: number;
	freshEbmFortifiedWithHM: number;
	freshEbmFortifiedWithoutHM: number;
	frozenEbm: number;
	thawedDbm: number;
	thawedDbmFortifiedWithHM: number;
	thawedDbmFortifiedWithoutHM: number;
	stableDbmOpen: number;
	stableDbmFortifiedWithHM: number;
	stableDbmFortifiedWithoutHM: number;
	thawedEbm: number;
	thawedEbmFortified: number;
	water: number;
	constructor(initial: IExpirationPolicy = {} as IExpirationPolicy) {
		if (!initial) {
			return;
		}

		Object.keys(initial).forEach((key) => {
			if (key === 'expirationHoursFormula') {
				this.formula = initial[key];
			} else if (key === 'expirationHoursPowderedFormulaOpened') {
				this.powderedFormulaOpened = initial[key];
			} else if (key === 'expirationHoursFreshEbm') {
				this.freshEbm = initial[key];
			} else if (key === 'expirationHoursFreshEbmFortifiedWithHM') {
				this.freshEbmFortifiedWithHM = initial[key];
			} else if (key === 'expirationHoursFreshEbmFortifiedWithoutHM') {
				this.freshEbmFortifiedWithoutHM = initial[key];
			} else if (key === 'expirationHoursFrozenEbm') {
				this.frozenEbm = initial[key];
			} else if (key === 'expirationHoursThawedDbm') {
				this.thawedDbm = initial[key];
			} else if (key === 'expirationHoursThawedDbmFortifiedWithHM') {
				this.thawedDbmFortifiedWithHM = initial[key];
			} else if (key === 'expirationHoursThawedDbmFortifiedWithoutHM') {
				this.thawedDbmFortifiedWithoutHM = initial[key];
			} else if (key === 'expirationHoursStableDbmOpen') {
				this.stableDbmOpen = initial[key];
			} else if (key === 'expirationHoursStableDbmFortifiedWithHM') {
				this.stableDbmFortifiedWithHM = initial[key];
			} else if (key === 'expirationHoursStableDbmFortifiedWithoutHM') {
				this.stableDbmFortifiedWithoutHM = initial[key];
			} else if (key === 'expirationHoursThawedEbm') {
				this.thawedEbm = initial[key];
			} else if (key === 'expirationHoursThawedEbmFortified') {
				this.thawedEbmFortified = initial[key];
			} else if (key === 'expirationHoursWater') this.water = initial[key];
			else {
				this[key] = initial[key];
			}
		});
	}

	toJSON(): IExpirationPolicy {
		return {
			expirationHoursFormula: this.formula,
			expirationHoursPowderedFormulaOpened: this.powderedFormulaOpened,
			expirationHoursFreshEbm: this.freshEbm,
			expirationHoursFreshEbmFortifiedWithHM: this.freshEbmFortifiedWithHM,
			expirationHoursFreshEbmFortifiedWithoutHM:
				this.freshEbmFortifiedWithoutHM,
			expirationHoursFrozenEbm: this.frozenEbm,
			expirationHoursThawedDbm: this.thawedDbm,
			expirationHoursThawedDbmFortifiedWithHM: this.thawedDbmFortifiedWithHM,
			expirationHoursThawedDbmFortifiedWithoutHM:
				this.thawedDbmFortifiedWithoutHM,
			expirationHoursStableDbmOpen: this.stableDbmOpen,
			expirationHoursStableDbmFortifiedWithHM: this.stableDbmFortifiedWithHM,
			expirationHoursStableDbmFortifiedWithoutHM:
				this.stableDbmFortifiedWithoutHM,
			expirationHoursThawedEbm: this.thawedEbm,
			expirationHoursThawedEbmFortified: this.thawedEbmFortified,
			expirationHoursWater: this.water,
		};
	}
}

export interface IExpirationPolicy {
	expirationHoursFormula: number;
	expirationHoursPowderedFormulaOpened: number;
	expirationHoursFreshEbm: number;
	expirationHoursStableDbmOpen: number;
	expirationHoursStableDbmFortifiedWithHM: number;
	expirationHoursStableDbmFortifiedWithoutHM: number;
	expirationHoursFreshEbmFortifiedWithHM: number;
	expirationHoursFreshEbmFortifiedWithoutHM: number;
	expirationHoursFrozenEbm: number;
	expirationHoursThawedDbm: number;
	expirationHoursThawedDbmFortifiedWithHM: number;
	expirationHoursThawedDbmFortifiedWithoutHM: number;
	expirationHoursThawedEbm: number;
	expirationHoursThawedEbmFortified: number;
	expirationHoursWater: number;
}
